import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Grid,
  Typography,
  Stack,
  RatingProps,
  Rating,
  Chip,
  SxProps,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slider,
  Autocomplete,
  TypographyProps,
  Collapse,
} from '@mui/material';
import moment from 'moment';

import {
  createLessonTutorFeedback,
  getLessonTutorFeedbackRewards,
  getLessonTutorFeedbacksState,
  ILessonTutorFeedback,
  resetLessonTutorFeedbackRewardsData,
  TCreateLessonTutorFeedback,
  TLessonTutorFeedbackRewardItem,
  TUpdateLessonTutorFeedback,
  updateLessonTutorFeedbacks,
} from '../../../store/V2/LessonTutorFeedbacksSlice';
import {
  CustomCardContent,
  defaultRatingStyles,
  EmptyRateIcon,
  IFeedbackStudent,
  RateIcon,
} from './helper';
import { useAppSelector } from '../../../hooks';
import { TPAutoCompleteArrowIconComponent, TPTextField } from '../../Inputs';
import { getShortName } from '../../../helper';
import { IUser } from '../../../store/UserSlice';

interface AddTutorFeedbackFormProps {
  sectionId: string;
  students: IFeedbackStudent[];
  feedback?: ILessonTutorFeedback;
  onFeedbackAdded?: (feedback: ILessonTutorFeedback[]) => void;
  onFeedbackUpdated?: (id: string, feedbackData: ILessonTutorFeedback) => void;
}

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  isStudentAttended: 'Yes' | 'No';
  lessonExtension: number;
  lessonExtensionReason: string;
  studentAttentiveness: TSelectOption | null;
  graspingAndApplicationOfConceptTaught: TSelectOption | null;
  progressForThisTopicAndSyllabus: TSelectOption | null;
  homeworkCompletedFromPreviousLesson: TSelectOption | null;
  homeworkSetForThisLesson: 'Yes' | 'No';
  quizScore: string;
  quizScoreOutOf: string;
  overallStudentRating: number | null;
  feedbackRewards: string[];
  comments: string;
};

const actionButtonStyles: SxProps = {
  padding: { xs: '6px 12px', sm: '12px 24px' },
  borderRadius: '6px',
  fontWeight: 600,
  fontSize: '12px',
};

const commonRatingProps: RatingProps = {
  icon: RateIcon,
  emptyIcon: EmptyRateIcon,
};

const liProps: TypographyProps = {
  component: 'li',
  sx: {
    fontWeight: 500,
    fontSize: '13px',
  },
};

const defaultInputStyles: SxProps = {
  '& .MuiInputBase-root': {
    height: '52px',
  },
};

const defaultInputPropsStyles: SxProps = {
  backgroundColor: '#ffffff',
};

export const overallOutOfRating = 5;

const SLIDER_MARKS = [
  { value: 0, label: '0' },
  { value: 15, label: '15' },
  { value: 30, label: '30' },
  { value: 45, label: '45' },
  { value: 60, label: '60' },
  { value: 75, label: '75' },
  { value: 90, label: '90' },
  { value: 105, label: '105' },
  { value: 120, label: '120' },
];

const studentAttentivenessOptions = [
  { label: 'Very attentive', value: 'Very attentive' },
  { label: 'Attentive', value: 'Attentive' },
  { label: 'Not so attentive', value: 'Not so attentive' },
  { label: 'Should be more attentive', value: 'Should be more attentive' },
  { label: 'Not attentive at all', value: 'Not attentive at all' },
];

const graspingAndApplicationOfConceptsTaughtOptions = [
  { label: 'Excellent', value: 'Excellent' },
  { label: 'Very Good', value: 'Very Good' },
  { label: 'Good', value: 'Good' },
  { label: 'Fair', value: 'Fair' },
  { label: 'Poor', value: 'Poor' },
];

const progressForTopicAndSyllabusOptions = [
  { label: 'Excellent', value: 'Excellent' },
  { label: 'Very Good', value: 'Very Good' },
  { label: 'Good', value: 'Good' },
  { label: 'Fair', value: 'Fair' },
  { label: 'Poor', value: 'Poor' },
];

const homeworkCompletedFromPreviousLessonOptions = [
  { label: 'Submitted On Time', value: 'Submitted On Time' },
  { label: 'Late submission', value: 'Late submission' },
  { label: 'Not submitted at all', value: 'Not submitted at all' },
  { label: 'No Homework set', value: 'No Homework set' },
];

const initialState: TState = {
  isStudentAttended: 'Yes',
  lessonExtension: 0,
  lessonExtensionReason: '',
  studentAttentiveness: null,
  graspingAndApplicationOfConceptTaught: null,
  progressForThisTopicAndSyllabus: null,
  homeworkCompletedFromPreviousLesson: null,
  homeworkSetForThisLesson: 'Yes',
  quizScore: '',
  quizScoreOutOf: '10',
  overallStudentRating: null,
  feedbackRewards: [],
  comments: '',
};

const AddTutorFeedbackForm: FunctionComponent<AddTutorFeedbackFormProps> = ({
  sectionId,
  students,
  feedback,
  onFeedbackAdded,
  onFeedbackUpdated,
}) => {
  const dispatch = useDispatch();

  // Store
  const lessonTutorFeedbacksState = useAppSelector(
    getLessonTutorFeedbacksState
  );

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const mode: 'new' | 'edit' = feedback ? 'edit' : 'new';
  const isLessonExtensionSliderDisabled = state.isStudentAttended === 'No';
  const isDetailedFeedbackVisible = state.isStudentAttended === 'Yes';
  const isLessonExtensionReasonInputDisabled =
    state.isStudentAttended === 'No' || !state.lessonExtension;

  let selectedFeedbackRewardData: TLessonTutorFeedbackRewardItem | undefined;
  let isSubmitDisabled = false;
  let lastUpdatedAt = '';
  let createdAt = '';
  let feedbackStudent: IUser | undefined;

  if (
    lessonTutorFeedbacksState.lessonTutorFeedbackRewardsData &&
    lessonTutorFeedbacksState.lessonTutorFeedbackRewardsData[
      state.overallStudentRating || 0
    ]
  ) {
    selectedFeedbackRewardData =
      lessonTutorFeedbacksState.lessonTutorFeedbackRewardsData[
        state.overallStudentRating || 0
      ];
  }

  if (state.isStudentAttended === 'Yes') {
    isSubmitDisabled =
      Boolean(
        state.lessonExtension && !state.lessonExtensionReason.trim().length
      ) ||
      !state.studentAttentiveness ||
      !state.graspingAndApplicationOfConceptTaught ||
      !state.progressForThisTopicAndSyllabus ||
      !state.homeworkCompletedFromPreviousLesson ||
      !state.quizScore ||
      !state.overallStudentRating ||
      !state.comments.trim().length;
  }

  if (feedback) {
    feedbackStudent = feedback.student as IUser;

    if (feedback.createdAt) {
      createdAt = moment(feedback.createdAt).format('ddd MMM DD YYYY, hh:mm A');
    }

    if (feedback.updatedAt) {
      lastUpdatedAt = moment(feedback.updatedAt).format(
        'ddd MMM DD YYYY, hh:mm A'
      );
    }
  }

  useEffect(() => {
    dispatch(getLessonTutorFeedbackRewards());

    if (mode === 'edit' && feedback) {
      setState((prevState) => {
        const newStateData = { ...prevState };

        if (feedback.isStudentAttended) {
          newStateData.isStudentAttended = feedback.isStudentAttended;
        }

        if (feedback.lessonExtension) {
          newStateData.lessonExtension = feedback.lessonExtension;
        }

        if (feedback.lessonExtensionReason) {
          newStateData.lessonExtensionReason = feedback.lessonExtensionReason;
        }

        if (feedback.studentAttentiveness) {
          newStateData.studentAttentiveness =
            studentAttentivenessOptions.find(
              (item) => item.value === feedback.studentAttentiveness
            ) || null;
        }

        if (feedback.graspingAndApplicationOfConceptTaught) {
          newStateData.graspingAndApplicationOfConceptTaught =
            graspingAndApplicationOfConceptsTaughtOptions.find(
              (item) =>
                item.value === feedback.graspingAndApplicationOfConceptTaught
            ) || null;
        }

        if (feedback.progressForThisTopicAndSyllabus) {
          newStateData.progressForThisTopicAndSyllabus =
            progressForTopicAndSyllabusOptions.find(
              (item) => item.value === feedback.progressForThisTopicAndSyllabus
            ) || null;
        }

        if (feedback.homeworkCompletedFromPreviousLesson) {
          newStateData.homeworkCompletedFromPreviousLesson =
            homeworkCompletedFromPreviousLessonOptions.find(
              (item) =>
                item.value === feedback.homeworkCompletedFromPreviousLesson
            ) || null;
        }

        if (feedback.homeworkSetForThisLesson) {
          newStateData.homeworkSetForThisLesson =
            feedback.homeworkSetForThisLesson;
        }

        if (feedback.quizScore) {
          newStateData.quizScore = feedback.quizScore.toString();
        }

        if (feedback.quizScoreOutOf) {
          newStateData.quizScoreOutOf = feedback.quizScoreOutOf.toString();
        }

        if (feedback.overallStudentRating) {
          newStateData.overallStudentRating = feedback.overallStudentRating;
        }

        if (feedback.feedbackRewards) {
          newStateData.feedbackRewards = feedback.feedbackRewards;
        }

        if (feedback.comments) {
          newStateData.comments = feedback.comments;
        }

        return newStateData;
      });
    }

    return () => {
      dispatch(resetLessonTutorFeedbackRewardsData());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const onFeedbackRewardOptionChange = (option: string) => {
    setState((prevState) => {
      let newList = [...prevState.feedbackRewards];

      if (newList.includes(option)) {
        newList = newList.filter((item) => item !== option);
      } else {
        newList.push(option);
      }

      return {
        ...prevState,
        feedbackRewards: newList,
      };
    });
  };

  const handleSubmit = async () => {
    if (mode === 'new' && students.length) {
      setLoading(true);

      const apiData: TCreateLessonTutorFeedback = {
        students: students.map((item) => item._id),
        section: sectionId,
        isStudentAttended: state.isStudentAttended,
      };

      if (apiData.isStudentAttended === 'Yes') {
        apiData.lessonExtension = state.lessonExtension;
        apiData.lessonExtensionReason = state.lessonExtensionReason;
        apiData.homeworkSetForThisLesson = state.homeworkSetForThisLesson;
        apiData.quizScore = parseFloat(state.quizScore);
        apiData.quizScoreOutOf = parseFloat(state.quizScoreOutOf);
        apiData.feedbackRewards = state.feedbackRewards;

        if (state.studentAttentiveness) {
          apiData.studentAttentiveness = state.studentAttentiveness.value;
        }

        if (state.graspingAndApplicationOfConceptTaught) {
          apiData.graspingAndApplicationOfConceptTaught =
            state.graspingAndApplicationOfConceptTaught.value;
        }

        if (state.progressForThisTopicAndSyllabus) {
          apiData.progressForThisTopicAndSyllabus =
            state.progressForThisTopicAndSyllabus.value;
        }

        if (state.homeworkCompletedFromPreviousLesson) {
          apiData.homeworkCompletedFromPreviousLesson =
            state.homeworkCompletedFromPreviousLesson.value;
        }

        if (state.overallStudentRating) {
          apiData.overallStudentRating = state.overallStudentRating;
        }

        if (state.comments.trim().length) {
          apiData.comments = state.comments.trim();
        }
      }

      const res: any = await dispatch(createLessonTutorFeedback(apiData));

      if (res.payload && onFeedbackAdded) {
        onFeedbackAdded(res.payload);
      }

      setLoading(false);
    } else if (mode === 'edit' && feedback) {
      setLoading(true);

      const apiData: TUpdateLessonTutorFeedback = {
        id: feedback._id,
        isStudentAttended: state.isStudentAttended,
      };

      if (apiData.isStudentAttended === 'Yes') {
        apiData.lessonExtension = state.lessonExtension;
        apiData.lessonExtensionReason = state.lessonExtensionReason;
        apiData.homeworkSetForThisLesson = state.homeworkSetForThisLesson;
        apiData.quizScore = parseFloat(state.quizScore);
        apiData.quizScoreOutOf = parseFloat(state.quizScoreOutOf);
        apiData.feedbackRewards = state.feedbackRewards;

        if (state.studentAttentiveness) {
          apiData.studentAttentiveness = state.studentAttentiveness.value;
        }

        if (state.graspingAndApplicationOfConceptTaught) {
          apiData.graspingAndApplicationOfConceptTaught =
            state.graspingAndApplicationOfConceptTaught.value;
        }

        if (state.progressForThisTopicAndSyllabus) {
          apiData.progressForThisTopicAndSyllabus =
            state.progressForThisTopicAndSyllabus.value;
        }

        if (state.homeworkCompletedFromPreviousLesson) {
          apiData.homeworkCompletedFromPreviousLesson =
            state.homeworkCompletedFromPreviousLesson.value;
        }

        if (state.overallStudentRating) {
          apiData.overallStudentRating = state.overallStudentRating;
        }

        if (state.comments.trim().length) {
          apiData.comments = state.comments.trim();
        }
      }

      const res: any = await dispatch(updateLessonTutorFeedbacks(apiData));

      if (res.payload && onFeedbackUpdated) {
        onFeedbackUpdated(feedback._id, res.payload);
      }

      setLoading(false);
    }
  };

  return (
    <div>
      <Grid container rowSpacing="10px">
        <Grid item xs={12}>
          <CustomCardContent>
            <Stack alignItems="center">
              <Typography fontSize="16px" fontWeight={600} mb="7px">
                Did the student attend? *
              </Typography>
              <RadioGroup
                row
                value={state.isStudentAttended}
                onChange={(e) =>
                  updateStateAttributes({
                    isStudentAttended: e.target
                      .value as TState['isStudentAttended'],
                  })
                }
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </Stack>
          </CustomCardContent>
        </Grid>
        {state.isStudentAttended === 'Yes' ? (
          <>
            <Grid item xs={12}>
              <CustomCardContent>
                <Stack alignItems="center">
                  <Typography fontSize="16px" fontWeight={600} mb="7px">
                    If lesson was extended (Additional minutes)
                  </Typography>
                  <Slider
                    min={0}
                    max={120}
                    step={null}
                    marks={SLIDER_MARKS}
                    value={state.lessonExtension}
                    disabled={isLessonExtensionSliderDisabled}
                    aria-labelledby="discrete-slider-restrict"
                    style={{
                      opacity: isLessonExtensionSliderDisabled ? '0.5' : '1',
                    }}
                    onChange={(e, val) => {
                      updateStateAttributes({
                        lessonExtension: val as number,
                      });
                    }}
                  />
                </Stack>
              </CustomCardContent>
            </Grid>
            <Grid item xs={12}>
              <CustomCardContent>
                <Stack alignItems="center">
                  <Typography fontSize="16px" fontWeight={600} mb="7px">
                    Lesson extension reason
                  </Typography>
                  <TPTextField
                    fullWidth
                    disabled={isLessonExtensionReasonInputDisabled}
                    value={state.lessonExtensionReason}
                    size="small"
                    placeholder="Lesson extension reason"
                    sx={{ ...defaultInputStyles }}
                    InputProps={{ sx: { ...defaultInputPropsStyles } }}
                    onChange={(e) =>
                      updateStateAttributes({
                        lessonExtensionReason: e.target.value,
                      })
                    }
                  />
                </Stack>
              </CustomCardContent>
            </Grid>
          </>
        ) : null}
        {isDetailedFeedbackVisible ? (
          <>
            <Grid item xs={12}>
              <CustomCardContent>
                <Stack alignItems="center">
                  <Typography fontSize="16px" fontWeight={600} mb="7px">
                    Student&apos;s attentiveness *
                  </Typography>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={state.studentAttentiveness}
                    options={studentAttentivenessOptions}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    onChange={(e, value) =>
                      updateStateAttributes({ studentAttentiveness: value })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        sx={{ ...defaultInputStyles }}
                        InputProps={{
                          ...params.InputProps,
                          sx: { ...defaultInputPropsStyles },
                        }}
                        size="small"
                        placeholder="Select"
                      />
                    )}
                  />
                </Stack>
              </CustomCardContent>
            </Grid>
            <Grid item xs={12}>
              <CustomCardContent>
                <Stack alignItems="center">
                  <Typography fontSize="16px" fontWeight={600} mb="7px">
                    Grasping and application of concepts taught *
                  </Typography>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={state.graspingAndApplicationOfConceptTaught}
                    options={graspingAndApplicationOfConceptsTaughtOptions}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    onChange={(e, value) =>
                      updateStateAttributes({
                        graspingAndApplicationOfConceptTaught: value,
                      })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        sx={{ ...defaultInputStyles }}
                        InputProps={{
                          ...params.InputProps,
                          sx: { ...defaultInputPropsStyles },
                        }}
                        size="small"
                        placeholder="Select"
                      />
                    )}
                  />
                </Stack>
              </CustomCardContent>
            </Grid>
            <Grid item xs={12}>
              <CustomCardContent>
                <Stack alignItems="center">
                  <Typography fontSize="16px" fontWeight={600} mb="7px">
                    Progress for this topic and syllabus *
                  </Typography>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={state.progressForThisTopicAndSyllabus}
                    options={progressForTopicAndSyllabusOptions}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    onChange={(e, value) =>
                      updateStateAttributes({
                        progressForThisTopicAndSyllabus: value,
                      })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        sx={{ ...defaultInputStyles }}
                        InputProps={{
                          ...params.InputProps,
                          sx: { ...defaultInputPropsStyles },
                        }}
                        size="small"
                        placeholder="Select"
                      />
                    )}
                  />
                </Stack>
              </CustomCardContent>
            </Grid>
            <Grid item xs={12}>
              <CustomCardContent>
                <Stack alignItems="center">
                  <Typography fontSize="16px" fontWeight={600} mb="7px">
                    Homework completed from previous lesson *
                  </Typography>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={state.homeworkCompletedFromPreviousLesson}
                    options={homeworkCompletedFromPreviousLessonOptions}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    onChange={(e, value) =>
                      updateStateAttributes({
                        homeworkCompletedFromPreviousLesson: value,
                      })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        sx={{ ...defaultInputStyles }}
                        InputProps={{
                          ...params.InputProps,
                          sx: { ...defaultInputPropsStyles },
                        }}
                        size="small"
                        placeholder="Select"
                      />
                    )}
                  />
                </Stack>
              </CustomCardContent>
            </Grid>
            <Grid item xs={12}>
              <CustomCardContent>
                <Stack alignItems="center">
                  <Typography fontSize="16px" fontWeight={600} mb="7px">
                    Homework set for this lesson? *
                  </Typography>
                  <RadioGroup
                    row
                    value={state.homeworkSetForThisLesson}
                    onChange={(e) =>
                      updateStateAttributes({
                        homeworkSetForThisLesson: e.target
                          .value as TState['homeworkSetForThisLesson'],
                      })
                    }
                  >
                    <FormControlLabel
                      value="Yes"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="No"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </Stack>
              </CustomCardContent>
            </Grid>
            <Grid item xs={12}>
              <CustomCardContent>
                <Stack alignItems="center">
                  <Typography fontSize="16px" fontWeight={600} mb="7px">
                    Quiz score *
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    mb="15px"
                  >
                    <TPTextField
                      fullWidth
                      type="number"
                      value={state.quizScore}
                      size="small"
                      placeholder="Quiz score"
                      sx={{ ...defaultInputStyles, maxWidth: '140px' }}
                      InputProps={{ sx: { ...defaultInputPropsStyles } }}
                      onChange={(e) =>
                        updateStateAttributes({
                          quizScore: e.target.value,
                        })
                      }
                    />
                    <Typography
                      fontSize="16px"
                      fontWeight={600}
                      sx={{ m: '0 15px' }}
                    >
                      /
                    </Typography>
                    <TPTextField
                      fullWidth
                      type="number"
                      defaultValue={state.quizScoreOutOf}
                      size="small"
                      placeholder="Quiz score out of"
                      sx={{ ...defaultInputStyles, maxWidth: '140px' }}
                      InputProps={{
                        readOnly: true,
                        sx: { ...defaultInputPropsStyles },
                      }}
                    />
                  </Stack>
                  <Stack>
                    <Typography component="ul" style={{ margin: '0' }}>
                      <Typography {...liProps}>
                        Oral or written quiz or based on responses during
                        lesson.
                      </Typography>
                      <Typography {...liProps}>
                        5-10 mins quiz duration at the end of the lesson.
                      </Typography>
                      <Typography {...liProps}>
                        Linked to&nbsp;
                        <a
                          href="https://docs.google.com/document/d/10Pi7xGz90il_GZCP-mjcC3sUvuf6EM_-_2i9AycYyQw/edit?usp=sharing"
                          rel="noopener noreferrer"
                          target="_blank"
                          style={{
                            margin: '0',
                            color: '#01B8AF',
                            textDecoration: 'underline',
                          }}
                        >
                          Progress Tracker
                        </a>
                        &nbsp; feature on student&apos;s account.
                      </Typography>
                      <Typography {...liProps}>
                        Mandatory to provide a &apos;Score&apos; even if no quiz
                        held.
                      </Typography>
                      <Typography {...liProps}>
                        This &apos;Score&apos; should indicate student&apos;s
                        level of understanding of this lesson.
                      </Typography>
                    </Typography>
                  </Stack>
                </Stack>
              </CustomCardContent>
            </Grid>
            <Grid item xs={12}>
              <CustomCardContent>
                <Typography fontSize="16px" fontWeight={600} textAlign="center">
                  Overall rating *
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography fontWeight={900} fontSize="30px">
                    <span style={{ fontSize: '50px' }}>
                      {state.overallStudentRating || 0}
                    </span>
                    /{overallOutOfRating}
                  </Typography>
                  <Rating
                    {...commonRatingProps}
                    value={state.overallStudentRating}
                    sx={{ ml: '10px', ...defaultRatingStyles }}
                    onChange={(e, newValue) => {
                      updateStateAttributes({
                        overallStudentRating: newValue,
                        feedbackRewards: [],
                      });
                    }}
                  />
                </Stack>
                <Collapse
                  in={Boolean(
                    selectedFeedbackRewardData &&
                      selectedFeedbackRewardData.list &&
                      selectedFeedbackRewardData.list.length
                  )}
                >
                  {selectedFeedbackRewardData &&
                  selectedFeedbackRewardData.list &&
                  selectedFeedbackRewardData.list.length ? (
                    <Stack>
                      <Typography
                        fontSize="16px"
                        fontWeight={600}
                        textAlign="center"
                        mb="7px"
                      >
                        {selectedFeedbackRewardData.title || ''}
                      </Typography>
                      <Stack
                        gap="5px"
                        direction="row"
                        flexWrap="wrap"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {selectedFeedbackRewardData.list.map((item) => {
                          const isSelected = state.feedbackRewards.includes(
                            item
                          );
                          const chipStyles: SxProps = {
                            fontWeight: 500,
                            fontSize: '12px',
                            borderRadius: '20px',
                          };

                          if (isSelected) {
                            chipStyles.backgroundColor = '#01A79F';
                            chipStyles.color = '#F8FAFC';
                            chipStyles['&:hover'] = {
                              backgroundColor: '#01A79F',
                              color: '#F8FAFC',
                            };
                          }

                          return (
                            <Chip
                              key={item}
                              label={item}
                              sx={chipStyles}
                              onClick={() => onFeedbackRewardOptionChange(item)}
                            />
                          );
                        })}
                      </Stack>
                    </Stack>
                  ) : null}
                </Collapse>
              </CustomCardContent>
            </Grid>
            <Grid item xs={12}>
              <CustomCardContent>
                <Stack alignItems="center">
                  <Typography fontSize="16px" fontWeight={600} mb="7px">
                    Detailed feedback *
                  </Typography>
                  <TPTextField
                    fullWidth
                    multiline
                    rows={3}
                    value={state.comments}
                    placeholder="Share details with our team"
                    InputProps={{
                      sx: { backgroundColor: '#ffffff' },
                    }}
                    onChange={(e) =>
                      updateStateAttributes({
                        comments: e.target.value,
                      })
                    }
                  />
                </Stack>
              </CustomCardContent>
            </Grid>
          </>
        ) : null}
        {feedback ? (
          <Grid item xs={12}>
            <CustomCardContent>
              <Stack alignItems="center" gap="10px">
                {createdAt ? (
                  <Typography fontWeight={500} fontSize="14px" color="#647488">
                    Created at: {createdAt}
                  </Typography>
                ) : null}
                {lastUpdatedAt ? (
                  <Typography fontWeight={500} fontSize="14px" color="#647488">
                    Last updated at: {lastUpdatedAt}
                  </Typography>
                ) : null}
                {feedbackStudent ? (
                  <Chip
                    key={feedbackStudent._id}
                    label={getShortName(
                      feedbackStudent.firstname,
                      feedbackStudent.lastname
                    )}
                  />
                ) : null}
              </Stack>
            </CustomCardContent>
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end">
            <LoadingButton
              disableElevation
              variant="contained"
              color="primary"
              disabled={isSubmitDisabled}
              loading={isLoading}
              sx={{ ...actionButtonStyles }}
              onClick={handleSubmit}
            >
              {mode === 'edit' ? 'Save Feedback' : 'Add Feedback'}
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </div>
  );
};

export default AddTutorFeedbackForm;
